import { useEffect, useState } from "react";
import { BarChart3, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Send, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getAppDailyReport, postAppDailyReport, setAppDailyReportSettings } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const MTD_KEYS = new Set(["mtd_new_applications", "mtd_conditional_offer"]);

// London-local YYYY-MM-DD for today (the server also anchors on London),
// used to bound the ▶ nav so ops can't accidentally build a future report.
const londonToday = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === "year").value;
  const m = parts.find(p => p.type === "month").value;
  const d = parts.find(p => p.type === "day").value;
  return `${y}-${m}-${d}`;
};

const shiftDate = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

export const DailyReportPopover = ({ actor }) => {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [autoPost, setAutoPost] = useState(false);
  const [activeDate, setActiveDate] = useState(null); // null → server-picked "today" (London)

  const load = async (date) => {
    setLoading(true);
    try {
      const r = await getAppDailyReport(date || undefined);
      setReport(r);
      setValues(Object.fromEntries(r.rows.map(row => [row.key, row.value])));
      setAutoPost(!!r.auto_post_enabled);
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(activeDate); }, [open, activeDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setPosting(true);
    try {
      const numeric = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, Number(v) || 0]));
      const r = await postAppDailyReport({ date: report.date, values: numeric, actor });
      toast.success(`Report for ${report.display_date} posted to #uk-work-group`);
      setReport(rep => ({ ...rep, posted: r }));
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message));
    } finally { setPosting(false); }
  };

  const toggleAuto = async (v) => {
    setAutoPost(v);
    try {
      await setAppDailyReportSettings({ auto_post_enabled: v });
      toast.success(v ? "Auto-post on — report goes to #uk-work-group at 5pm on weekdays" : "Auto-post off");
    } catch (e) {
      setAutoPost(!v);
      toast.error(errorDetailMessage(e, e.message));
    }
  };

  const todayIso = londonToday();
  const currentDate = report?.date || activeDate || todayIso;
  const isToday = currentDate === todayIso;
  const isPastMissed = !isToday && report && !report.posted;
  const goPrev = () => setActiveDate(shiftDate(currentDate, -1));
  const goNext = () => { if (!isToday) setActiveDate(shiftDate(currentDate, 1)); };
  const goToday = () => setActiveDate(null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1" data-testid="daily-report-btn">
          <BarChart3 className="w-3.5 h-3.5 text-blue-700" />
          <span className="text-[11px]">Today's report</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="daily-report-popover">
        {loading || !report ? (
          <div className="p-10 text-center" data-testid="daily-report-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
            <div className="text-[11px] text-muted-foreground mt-2">Counting today's Monday board activity…</div>
          </div>
        ) : (
          <div>
            <div className="px-3 py-2 border-b flex items-center justify-between bg-slate-50 rounded-t-md">
              <div className="text-xs font-semibold">Applications Daily Report</div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={goPrev}
                  className="p-0.5 rounded hover:bg-slate-200 text-slate-600"
                  title="Previous day"
                  data-testid="daily-report-prev-day-btn">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <div
                  className={cn("text-[11px] font-mono font-semibold tabular-nums px-1 cursor-pointer select-none",
                    !isToday && "text-blue-700 underline decoration-dotted")}
                  onClick={goToday}
                  title={isToday ? "Today (London)" : "Click to jump back to today"}
                  data-testid="daily-report-active-date">
                  {report.display_date}
                </div>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={isToday}
                  className={cn("p-0.5 rounded text-slate-600",
                    isToday ? "opacity-30 cursor-not-allowed" : "hover:bg-slate-200")}
                  title={isToday ? "Already on today" : "Next day"}
                  data-testid="daily-report-next-day-btn">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {isPastMissed && (
              <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 flex items-start gap-1.5 text-[11px] text-amber-900"
                data-testid="daily-report-missed-warning">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <span>Auto-post did not complete for this day — review the counts below and click <b>Submit to Slack</b> to send it now.</span>
              </div>
            )}
            {report.funding && (() => {
              const f = report.funding; const c = f.currency || "£";
              const k = (v) => Math.abs(v || 0) >= 10000 ? `${c}${Math.round((v || 0) / 1000).toLocaleString("en-GB")}k` : `${c}${Math.round(v || 0).toLocaleString("en-GB")}`;
              return (
                <div className="px-3 py-2 border-b bg-emerald-50/60 text-[11px] space-y-1" data-testid="daily-report-funding-block">
                  <div className="font-semibold text-emerald-900">Funding &amp; pipeline</div>
                  {f.mtd ? (
                    <div data-testid="daily-report-funded-mtd">Funded MTD <b>{k(f.mtd.funded)}</b> · {f.mtd.deals} deal(s) · Cost of sales MTD <b>{k(f.mtd.cost_of_sales)}</b> ({f.mtd.cost_of_sales_pct ?? 0}%)</div>
                  ) : <div className="text-amber-700">Funded MTD unavailable</div>}
                  {f.pipeline ? (
                    <>
                      <div data-testid="daily-report-pipeline">FC <b>{k(f.pipeline.fc.value)}</b> ({f.pipeline.fc.count}) · OA <b>{k(f.pipeline.oa.value)}</b> ({f.pipeline.oa.count}) · CO <b>{k(f.pipeline.co_total.value)}</b> ({f.pipeline.co_total.count})</div>
                      {f.pipeline.co.length > 0 && (
                        <div className="text-muted-foreground" data-testid="daily-report-co-breakdown">
                          {f.pipeline.co.map((row) => `${k(row.value)} at ${row.status}`).join(" · ")}
                        </div>
                      )}
                    </>
                  ) : <div className="text-amber-700">Pipeline unavailable (Monday)</div>}
                </div>
              );
            })()}
            <div className="divide-y">
              {report.rows.map(row => (
                <div key={row.key}
                  className={cn("flex items-center justify-between px-3 py-1",
                    MTD_KEYS.has(row.key) && "bg-blue-50/50")}
                  data-testid={`daily-report-row-${row.key}`}>
                  <span className={cn("text-xs", MTD_KEYS.has(row.key) && "font-semibold")}
                    title={row.key === "sf_enquiry" ? "Created today as Direct or via a SimplyFunded broker" : ""}>
                    {row.label}
                  </span>
                  <input type="number" min={0}
                    value={values[row.key] ?? 0}
                    onChange={(e) => setValues(v => ({ ...v, [row.key]: e.target.value }))}
                    className={cn("w-20 h-6 text-right text-xs border rounded px-1.5 tabular-nums",
                      MTD_KEYS.has(row.key) && "font-semibold")}
                    data-testid={`daily-report-input-${row.key}`} />
                </div>
              ))}
            </div>
            <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t">
              Simply Funded Enquiry = created today as Direct or via a SimplyFunded broker. All numbers are editable before posting.
            </div>
            {(report.co_deals || []).length > 0 && (
              <div className="border-t" data-testid="daily-report-co-deals">
                <div className="px-3 pt-1.5 text-[11px] font-semibold text-emerald-700">
                  Conditional Offers today ({report.co_deals.length})
                </div>
                <div className="px-3 pb-1.5 max-h-40 overflow-y-auto">
                  {report.co_deals.map((n) => (
                    <div key={n} className="text-[11px] text-slate-600 truncate py-0.5" title={n}>• {n}</div>
                  ))}
                </div>
              </div>
            )}
            {(report.co_repeats || []).length > 0 && (
              <div className="border-t" data-testid="daily-report-co-repeats">
                <div className="px-3 pt-1.5 text-[11px] font-semibold text-amber-700">
                  Previously offered & rejected — verify not duplicate sends ({report.co_repeats.length})
                </div>
                <div className="px-3 pb-1.5 max-h-24 overflow-y-auto">
                  {report.co_repeats.map((n) => (
                    <div key={n} className="text-[11px] text-slate-500 truncate py-0.5" title={n}>• {n}</div>
                  ))}
                </div>
              </div>
            )}
            {report.trend && (
              <div className="px-3 py-1.5 border-t text-[11px] flex items-center gap-1.5" data-testid="daily-report-trend">
                {(report.trend.pct_change ?? 0) >= 0
                  ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  : <TrendingDown className="w-3.5 h-3.5 text-rose-600 shrink-0" />}
                <span>
                  New apps this week avg <b>{report.trend.this_week_avg}</b>/day vs last week <b>{report.trend.last_week_avg}</b>/day
                  {report.trend.pct_change != null && (
                    <b className={report.trend.pct_change >= 0 ? "text-emerald-700" : "text-rose-700"}>
                      {" "}({report.trend.pct_change >= 0 ? "+" : ""}{report.trend.pct_change}%)
                    </b>
                  )}
                </span>
              </div>
            )}
            <div className="px-3 py-2 border-t flex items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                <Switch checked={autoPost} onCheckedChange={toggleAuto} className="scale-75"
                  data-testid="daily-report-autopost-switch" />
                Auto-post 5pm
              </label>
              <Button size="sm" className="h-7 text-[11px] bg-blue-700 hover:bg-blue-800"
                onClick={submit} disabled={posting} data-testid="daily-report-submit-btn">
                {posting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Submit to Slack
              </Button>
            </div>
            {report.posted && (
              <div className="px-3 py-1.5 border-t text-[10px] text-emerald-700 flex items-center gap-1"
                data-testid="daily-report-posted-note">
                <CheckCircle2 className="w-3 h-3" />
                Posted {new Date(report.posted.posted_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} by {report.posted.posted_by}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
